import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import api from "@/lib/api";

export default function Certificate() {
  const { pid, rid } = useParams();
  const { setCrumbs } = useOutletContext();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [product, setProduct] = useState(null);
  const [progress, setProgress] = useState({});
  const [certs, setCerts] = useState([]);

  const load = useCallback(() => {
    api.get(`/certificates/${pid}/${rid}`)
      .then((res) => {
        setCert(res.data);
        setCrumbs([
          { label: res.data.productTag === "CORE" ? "EQUIPR CORE" : res.data.productTag, to: `/${pid}` },
          { label: res.data.roleName, to: `/${pid}/${rid}` },
          { label: "Certificate", to: null },
        ]);
      })
      .catch(() => navigate(`/${pid}/${rid}`, { replace: true }));
    api.get(`/catalog/${pid}`).then((r) => setProduct(r.data));
    api.get(`/progress/${pid}`).then((r) => setProgress(r.data.roles));
    api.get(`/certificates`).then((r) => setCerts(r.data.certificates));
  }, [pid, rid, navigate, setCrumbs]);

  useEffect(() => { load(); }, [load]);

  if (!cert || !product) return <div className="loading">Loading the certificate…</div>;

  const certifiedRoleIds = new Set(certs.filter((c) => c.productId === pid).map((c) => c.roleId));
  const verifyUrl = `${window.location.origin}/c/${cert.verifyCode}`;

  const roleState = (r) => {
    if (certifiedRoleIds.has(r.id)) return { cls: "sdot--done", label: "Certified" };
    const prog = progress[r.id];
    if (prog && prog.doneCount > 0) return { cls: "sdot--progress", label: `${prog.doneCount}/${prog.moduleCount} in progress` };
    return { cls: "sdot--none", label: "Not started" };
  };

  return (
    <section className="page" data-testid="certificate-page">
      <div className="container cert-grid">
        <div className="cert" data-testid="certificate-card">
          <span className="cert__mark">EQUIPR</span>
          <div className="cert__kicker">Academy · Certificate</div>
          <p className="cert__lead">This certifies that</p>
          <div className="cert__name" data-testid="cert-name">{cert.learnerName}</div>
          <p className="cert__lead">has completed the</p>
          <div className="cert__title" data-testid="cert-title">{product.name} — {cert.roleName} Certification</div>
          <div className="cert__meta">
            <div>
              <div className="cert__meta-label">Issued</div>
              <div className="cert__meta-value">{cert.issuedDate}</div>
            </div>
            <div>
              <div className="cert__meta-label">Product version</div>
              <div className="cert__meta-value">{cert.productTag} · {cert.guideVersion}</div>
            </div>
            <div>
              <div className="cert__meta-label">Verify</div>
              <div className="cert__meta-value">/c/{cert.verifyCode}</div>
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: "var(--grid-gap)" }}>
          <div className="aftercard on-dark" data-testid="after-certification">
            <div className="aftercard__label">After certification</div>
            <p className="aftercard__body">
              This certificate is recorded against your account and visible to your account owner. Re-certification is due when {product.name} ships a major release, or twelve months from the issue date — whichever comes first.
            </p>
            <button className="btn btn--yellow btn--block" onClick={() => navigate(`/${pid}`)} data-testid="start-another-btn">Start another path</button>
          </div>

          <div className="sidecard">
            <div className="sidecard__label">My certifications · {product.tag}</div>
            {product.roles.map((r) => {
              const st = roleState(r);
              return (
                <button key={r.id} className="certlist__item" onClick={() => navigate(`/${pid}/${r.id}`)} style={{ background: "none", border: 0, borderBottom: "1px solid var(--divider)", width: "100%", cursor: "pointer", font: "inherit" }} data-testid={`certlist-${r.id}`}>
                  <span className={`sdot ${st.cls}`} />
                  <span className="certlist__name">{r.name}</span>
                  <span className="certlist__state">{st.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
